import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Building2, MapPin, BedDouble, Target, TrendingUp,
  Sparkles, Clock, Calendar, Eye, Users, Zap, ChevronLeft,
  Loader2, BarChart3, Globe2, Image, Video, LayoutGrid,
  Play, Save, Activity, CheckCircle2, Wifi, WifiOff, Languages,
  Filter, ChevronDown, ChevronUp, Info, FileText, Instagram, Facebook,
  Scale, Bookmark, X, Copy, Download, UserPlus, Megaphone, MessageCircle, Settings
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Checkbox } from "../components/ui/checkbox";
import { 
  PieChart as RePieChart, Pie, Cell, ResponsiveContainer, 
  Tooltip
} from "recharts";
import CampaignTracking from "./CampaignTracking";
import CompareRecommendations from "./CompareRecommendations";
import { useMeta } from "../context/MetaContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ['#ffc107', '#10b981', '#06b6d4', '#a855f7', '#f472b6', '#ef4444', '#3b82f6', '#ec4899', '#14b8a6', '#f59e0b'];

const translations = {
  ar: {
    appTitle: "HELCOO AdTarget Pro",
    appSubtitle: "نظام توجيه إعلانات العقارات في الإمارات",
    heroTitle: "وجّه إعلانك للجمهور الصحيح",
    heroSubtitle: "أدخل بيانات العقار والميزانية للحصول على توصيات ذكية",
    propertyInfo: "معلومات العقار",
    emirate: "الإمارة",
    selectEmirate: "اختر الإمارة",
    area: "المنطقة",
    selectArea: "اختر المنطقة",
    propertyType: "نوع العقار",
    selectPropertyType: "اختر نوع العقار",
    priceRange: "نطاق السعر (درهم)",
    priceFrom: "من",
    priceTo: "إلى",
    priceFromPlaceholder: "السعر الأدنى",
    priceToPlaceholder: "السعر الأقصى",
    bedroomsRange: "عدد الغرف",
    bedroomsFrom: "من",
    bedroomsTo: "إلى",
    bedroomsFromPlaceholder: "الحد الأدنى",
    bedroomsToPlaceholder: "الحد الأقصى",
    noBedroomsNeeded: "لا يحتاج غرف",
    budgetSection: "ميزانية الإعلان",
    totalBudget: "الميزانية الكلية (درهم)",
    campaignDuration: "مدة الحملة (أيام)",
    campaignObjectiveLabel: "هدف الحملة",
    leadGeneration: "جمع بيانات العملاء",
    awareness: "الترويج لمشروع",
    engagement: "زيادة التفاعل",
    selectObjective: "اختر هدف الحملة",
    getRecommendations: "احصل على التوصيات",
    analyzing: "جاري التحليل...",
    results: "التوصيات",
    targetCountries: "الدول المستهدفة",
    budgetDistribution: "توزيع الميزانية",
    adType: "نوع الإعلان المقترح",
    allAdTypes: "جميع أنواع الإعلانات",
    campaignObjective: "هدف الحملة",
    aiAnalysis: "HELCOO AI ANALYSES",
    adCopySuggestions: "نصوص إعلانية مقترحة",
    clickToCopy: "انقر للنسخ",
    copied: "تم النسخ!",
    expectedReach: "الوصول المتوقع",
    expectedLeads: "العملاء المتوقعين",
    days: "يوم",
    expectedCTR: "CTR المتوقع",
    budget: "الميزانية",
    reach: "الوصول",
    leads: "العملاء",
    bestTime: "أفضل وقت",
    editData: "تعديل البيانات",
    newAnalysis: "تحليل جديد",
    saveForTracking: "حفظ للتتبع",
    saveForComparison: "حفظ للمقارنة",
    compareRecommendations: "مقارنة التوصيات",
    enterName: "أدخل اسماً لهذه التوصية",
    namePlaceholder: "مثال: حملة دبي - يناير",
    saveAndCompare: "حفظ",
    cancel: "إلغاء",
    savedForComparison: "تم حفظ التوصية للمقارنة!",
    trackCampaigns: "تتبع الحملات",
    metaConnected: "Meta متصل",
    metaDisconnected: "Meta غير متصل",
    last30Days: "بيانات آخر 30 يوم",
    dateRange: "الفترة الزمنية",
    from: "من",
    to: "إلى",
    apply: "تطبيق",
    customDateRange: "فترة مخصصة",
    spend: "إنفاق",
    clicks: "نقرة",
    aed: "درهم",
    formTab: "بيانات العقار",
    resultsTab: "التوصيات",
    fillRequired: "يرجى ملء جميع الحقول المطلوبة",
    recommendationsSuccess: "تم إنشاء التوصيات بنجاح!",
    errorOccurred: "حدث خطأ أثناء الحصول على التوصيات",
    campaignSaved: "تم حفظ الحملة للتتبع!",
    footerText: "نظام توجيه إعلانات العقارات في الإمارات - مدعوم بالذكاء الاصطناعي",
    targetingOptions: "خيارات الاستهداف",
    selectRegions: "اختر المناطق الجغرافية",
    allRegions: "جميع المناطق",
    numCountries: "عدد الدول للتحليل",
    countriesAnalyzed: "دولة تم تحليلها",
    recommended: "موصى به",
    creativeTips: "نصائح إبداعية",
    minBudget: "الحد الأدنى للميزانية",
    bestPlatforms: "أفضل المنصات",
    buyerProfile: "ملف المشتري",
    showDetails: "عرض التفاصيل",
    hideDetails: "إخفاء التفاصيل",
    metaTargetingOptions: "خيارات استهداف Meta",
    interests: "الاهتمامات",
    behaviors: "السلوكيات",
    devices: "الأجهزة",
    ageRange: "الفئة العمرية",
    incomeLevel: "مستوى الدخل",
    placements: "المواضع",
    customAudiences: "الجماهير المخصصة",
    exportTargeting: "تصدير للحافظة",
    exportedSuccess: "تم نسخ خيارات الاستهداف!",
    copySection: "نسخ",
    selectCountries: "اختيار الدول المستهدفة",
    selectedCountries: "الدول المحددة",
    clearAll: "مسح الكل",
    countriesSelected: "دولة محددة",
    settings: "الإعدادات"
  },
  en: {
    appTitle: "HELCOO AdTarget Pro",
    appSubtitle: "UAE Real Estate Ad Targeting System",
    heroTitle: "Target the Right Audience",
    heroSubtitle: "Enter property details and budget for smart recommendations",
    propertyInfo: "Property Information",
    emirate: "Emirate",
    selectEmirate: "Select Emirate",
    area: "Area",
    selectArea: "Select Area",
    propertyType: "Property Type",
    selectPropertyType: "Select Property Type",
    priceRange: "Price Range (AED)",
    priceFrom: "From",
    priceTo: "To",
    priceFromPlaceholder: "Min Price",
    priceToPlaceholder: "Max Price",
    bedroomsRange: "Bedrooms",
    bedroomsFrom: "From",
    bedroomsTo: "To",
    bedroomsFromPlaceholder: "Min",
    bedroomsToPlaceholder: "Max",
    noBedroomsNeeded: "N/A",
    budgetSection: "Ad Budget",
    totalBudget: "Total Budget (AED)",
    campaignDuration: "Campaign Duration (Days)",
    campaignObjectiveLabel: "Campaign Objective",
    leadGeneration: "Lead Generation",
    awareness: "Brand Awareness",
    engagement: "Engagement",
    selectObjective: "Select Objective",
    getRecommendations: "Get Recommendations",
    analyzing: "Analyzing...",
    results: "Recommendations",
    targetCountries: "Target Countries",
    budgetDistribution: "Budget Distribution",
    adType: "Recommended Ad Type",
    allAdTypes: "All Ad Types",
    campaignObjective: "Campaign Objective",
    aiAnalysis: "HELCOO AI ANALYSES",
    adCopySuggestions: "Suggested Ad Copy",
    clickToCopy: "Click to copy",
    copied: "Copied!",
    expectedReach: "Expected Reach",
    expectedLeads: "Expected Leads",
    days: "days",
    expectedCTR: "Expected CTR",
    budget: "Budget",
    reach: "Reach",
    leads: "Leads",
    bestTime: "Best Time",
    editData: "Edit Data",
    newAnalysis: "New Analysis",
    saveForTracking: "Save for Tracking",
    saveForComparison: "Save for Comparison",
    compareRecommendations: "Compare Recommendations",
    enterName: "Enter a name for this recommendation",
    namePlaceholder: "e.g., Dubai Campaign - January",
    saveAndCompare: "Save",
    cancel: "Cancel",
    savedForComparison: "Recommendation saved for comparison!",
    trackCampaigns: "Track Campaigns",
    metaConnected: "Meta Connected",
    metaDisconnected: "Meta Disconnected",
    last30Days: "Last 30 days data",
    dateRange: "Date Range",
    from: "From",
    to: "To",
    apply: "Apply",
    customDateRange: "Custom Range",
    spend: "Spend",
    clicks: "Clicks",
    aed: "AED",
    formTab: "Property Data",
    resultsTab: "Recommendations",
    fillRequired: "Please fill all required fields",
    recommendationsSuccess: "Recommendations generated successfully!",
    errorOccurred: "An error occurred while getting recommendations",
    campaignSaved: "Campaign saved for tracking!",
    footerText: "UAE Real Estate Ad Targeting System - Powered by AI",
    targetingOptions: "Targeting Options",
    selectRegions: "Select Geographic Regions",
    allRegions: "All Regions",
    numCountries: "Countries to Analyze",
    countriesAnalyzed: "countries analyzed",
    recommended: "Recommended",
    creativeTips: "Creative Tips",
    minBudget: "Minimum Budget",
    bestPlatforms: "Best Platforms",
    buyerProfile: "Buyer Profile",
    showDetails: "Show Details",
    hideDetails: "Hide Details",
    metaTargetingOptions: "Meta Targeting Options",
    interests: "Interests",
    behaviors: "Behaviors",
    devices: "Devices",
    ageRange: "Age Range",
    incomeLevel: "Income Level",
    placements: "Placements",
    customAudiences: "Custom Audiences",
    exportTargeting: "Export to Clipboard",
    exportedSuccess: "Targeting options copied!",
    copySection: "Copy",
    selectCountries: "Select Target Countries",
    selectedCountries: "Selected Countries",
    clearAll: "Clear All",
    countriesSelected: "countries selected",
    settings: "Settings"
  }
};

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [activeTab, setActiveTab] = useState("form");
  const [showTracking, setShowTracking] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [metaInsights, setMetaInsights] = useState(null);
  const [metaDatePreset, setMetaDatePreset] = useState("last_30d");
  const [metaCustomDates, setMetaCustomDates] = useState({ since: "", until: "" });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [language, setLanguage] = useState("ar");
  const [emirates, setEmirates] = useState({});
  const [areas, setAreas] = useState({});
  const [propertyTypes, setPropertyTypes] = useState({});
  const [regions, setRegions] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expandedAdType, setExpandedAdType] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [savingForComparison, setSavingForComparison] = useState(false);
  
  // Use global Meta context instead of local state
  const { metaStatus } = useMeta();
  
  const [formData, setFormData] = useState({
    emirate: "",
    area: "",
    propertyType: "",
    priceFrom: "",
    priceTo: "",
    bedroomsFrom: "",
    bedroomsTo: "",
    budget: 3670,
    duration: 21,
    campaignObjective: "lead_generation",
    selectedRegions: [],
    selectedCountries: [],
    numCountries: 7
  });

  // Available countries for targeting
  const availableCountries = [
    { code: "russia", name_ar: "روسيا", name_en: "Russia", flag: "🇷🇺" },
    { code: "india", name_ar: "الهند", name_en: "India", flag: "🇮🇳" },
    { code: "united_kingdom", name_ar: "بريطانيا", name_en: "UK", flag: "🇬🇧" },
    { code: "saudi_arabia", name_ar: "السعودية", name_en: "Saudi Arabia", flag: "🇸🇦" },
    { code: "china", name_ar: "الصين", name_en: "China", flag: "🇨🇳" },
    { code: "pakistan", name_ar: "باكستان", name_en: "Pakistan", flag: "🇵🇰" },
    { code: "egypt", name_ar: "مصر", name_en: "Egypt", flag: "🇪🇬" },
    { code: "jordan", name_ar: "الأردن", name_en: "Jordan", flag: "🇯🇴" },
    { code: "lebanon", name_ar: "لبنان", name_en: "Lebanon", flag: "🇱🇧" },
    { code: "iraq", name_ar: "العراق", name_en: "Iraq", flag: "🇮🇶" },
    { code: "kuwait", name_ar: "الكويت", name_en: "Kuwait", flag: "🇰🇼" },
    { code: "qatar", name_ar: "قطر", name_en: "Qatar", flag: "🇶🇦" },
    { code: "bahrain", name_ar: "البحرين", name_en: "Bahrain", flag: "🇧🇭" },
    { code: "oman", name_ar: "عُمان", name_en: "Oman", flag: "🇴🇲" },
    { code: "usa", name_ar: "أمريكا", name_en: "USA", flag: "🇺🇸" },
    { code: "germany", name_ar: "ألمانيا", name_en: "Germany", flag: "🇩🇪" },
    { code: "france", name_ar: "فرنسا", name_en: "France", flag: "🇫🇷" },
    { code: "italy", name_ar: "إيطاليا", name_en: "Italy", flag: "🇮🇹" },
    { code: "turkey", name_ar: "تركيا", name_en: "Turkey", flag: "🇹🇷" },
    { code: "iran", name_ar: "إيران", name_en: "Iran", flag: "🇮🇷" },
    { code: "kazakhstan", name_ar: "كازاخستان", name_en: "Kazakhstan", flag: "🇰🇿" },
    { code: "nigeria", name_ar: "نيجيريا", name_en: "Nigeria", flag: "🇳🇬" },
    { code: "south_africa", name_ar: "جنوب أفريقيا", name_en: "South Africa", flag: "🇿🇦" },
    { code: "australia", name_ar: "أستراليا", name_en: "Australia", flag: "🇦🇺" },
    { code: "canada", name_ar: "كندا", name_en: "Canada", flag: "🇨🇦" },
    { code: "singapore", name_ar: "سنغافورة", name_en: "Singapore", flag: "🇸🇬" },
    { code: "malaysia", name_ar: "ماليزيا", name_en: "Malaysia", flag: "🇲🇾" },
    { code: "indonesia", name_ar: "إندونيسيا", name_en: "Indonesia", flag: "🇮🇩" },
    { code: "philippines", name_ar: "الفلبين", name_en: "Philippines", flag: "🇵🇭" },
    { code: "bangladesh", name_ar: "بنغلاديش", name_en: "Bangladesh", flag: "🇧🇩" }
  ];

  const toggleCountry = (code) => {
    setFormData(prev => ({
      ...prev,
      selectedCountries: prev.selectedCountries.includes(code)
        ? prev.selectedCountries.filter(c => c !== code)
        : [...prev.selectedCountries, code]
    }));
  };

  // Campaign objective options
  const campaignObjectives = [
    { value: "lead_generation", label_ar: "جمع بيانات العملاء", label_en: "Lead Generation", icon: UserPlus, color: "text-emerald-500", bgColor: "bg-emerald-500/10" },
    { value: "awareness", label_ar: "الترويج لمشروع", label_en: "Brand Awareness", icon: Megaphone, color: "text-amber-500", bgColor: "bg-amber-500/10" },
    { value: "engagement", label_ar: "زيادة التفاعل", label_en: "Engagement", icon: MessageCircle, color: "text-blue-500", bgColor: "bg-blue-500/10" }
  ];

  // Property types that don't need bedrooms
  const noBedroomTypes = ["studio", "land", "commercial", "office", "warehouse"];
  
  // Check if current property type needs bedrooms
  const needsBedrooms = formData.propertyType && !noBedroomTypes.includes(formData.propertyType);

  // Date preset options for Meta insights
  const datePresetOptions = [
    { value: "yesterday", label_ar: "أمس", label_en: "Yesterday" },
    { value: "last_7d", label_ar: "آخر 7 أيام", label_en: "Last 7 days" },
    { value: "last_14d", label_ar: "آخر 14 يوم", label_en: "Last 14 days" },
    { value: "last_30d", label_ar: "آخر 30 يوم", label_en: "Last 30 days" },
    { value: "last_60d", label_ar: "آخر 60 يوم", label_en: "Last 60 days" },
    { value: "last_90d", label_ar: "آخر 90 يوم", label_en: "Last 90 days" },
    { value: "last_120d", label_ar: "آخر 120 يوم", label_en: "Last 120 days" },
    { value: "last_150d", label_ar: "آخر 150 يوم", label_en: "Last 150 days" },
    { value: "last_180d", label_ar: "آخر 180 يوم", label_en: "Last 180 days" },
    { value: "custom", label_ar: "تاريخ مخصص", label_en: "Custom date" }
  ];

  const t = translations[language];
  const isRTL = language === "ar";

  useEffect(() => {
    fetchEmirates();
    fetchPropertyTypes();
    fetchRegions();
    fetchMetaInsights();
  }, []);

  useEffect(() => {
    if (formData.emirate) {
      fetchAreas(formData.emirate);
    }
  }, [formData.emirate]);

  const fetchEmirates = async () => {
    try {
      const response = await axios.get(`${API}/emirates`);
      setEmirates(response.data);
    } catch (error) {
      console.error("Error fetching emirates:", error);
    }
  };

  const fetchAreas = async (emirate) => {
    try {
      const response = await axios.get(`${API}/areas/${emirate}`);
      setAreas(response.data);
      setFormData(prev => ({ ...prev, area: "" }));
    } catch (error) {
      console.error("Error fetching areas:", error);
    }
  };

  const fetchPropertyTypes = async () => {
    try {
      const response = await axios.get(`${API}/property-types`);
      setPropertyTypes(response.data);
    } catch (error) {
      console.error("Error fetching property types:", error);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await axios.get(`${API}/regions`);
      setRegions(response.data);
    } catch (error) {
      console.error("Error fetching regions:", error);
    }
  };

  // Meta status is now managed by global context (useMeta hook)

  const fetchMetaInsights = async (preset = metaDatePreset, customDates = metaCustomDates) => {
    setLoadingInsights(true);
    try {
      let url = `${API}/meta/insights?date_preset=${preset}`;
      
      if (preset === "custom" && customDates.since && customDates.until) {
        url = `${API}/meta/insights?since=${customDates.since}&until=${customDates.until}`;
      }
      
      const response = await axios.get(url);
      if (response.data.success) {
        setMetaInsights(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching Meta insights:", error);
    } finally {
      setLoadingInsights(false);
    }
  };

  const handleDatePresetChange = (preset) => {
    setMetaDatePreset(preset);
    if (preset === "custom") {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
      fetchMetaInsights(preset);
    }
  };

  const applyCustomDateRange = () => {
    if (metaCustomDates.since && metaCustomDates.until) {
      fetchMetaInsights("custom", metaCustomDates);
      setShowDatePicker(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Calculate average price from range or use single value (parse from formatted string)
    const priceFrom = parseFloat(parseInputNumber(formData.priceFrom)) || 0;
    const priceTo = parseFloat(parseInputNumber(formData.priceTo)) || priceFrom;
    const avgPrice = priceTo > 0 ? (priceFrom + priceTo) / 2 : priceFrom;
    const budgetValue = parseFloat(parseInputNumber(formData.budget)) || 0;
    
    if (!formData.emirate || !formData.area || !formData.propertyType || !avgPrice || !budgetValue) {
      toast.error(t.fillRequired);
      return;
    }

    // Parse bedrooms range
    const bedroomsFrom = parseInt(formData.bedroomsFrom) || null;
    const bedroomsTo = parseInt(formData.bedroomsTo) || bedroomsFrom;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/recommendations`, {
        property: {
          emirate: formData.emirate,
          area: formData.area,
          property_type: formData.propertyType,
          price_aed: avgPrice,
          price_from_aed: priceFrom || null,
          price_to_aed: priceTo || null,
          bedrooms_from: bedroomsFrom,
          bedrooms_to: bedroomsTo
        },
        budget: {
          total_budget_aed: budgetValue,
          campaign_duration_days: formData.duration || null
        },
        campaign_objective: formData.campaignObjective,
        language: language,
        selected_regions: formData.selectedRegions.length > 0 ? formData.selectedRegions : null,
        selected_countries: formData.selectedCountries.length > 0 ? formData.selectedCountries : null,
        num_countries: formData.numCountries
      });

      setRecommendations(response.data);
      setActiveTab("results");
      toast.success(t.recommendationsSuccess);
    } catch (error) {
      console.error("Error:", error);
      toast.error(t.errorOccurred);
    } finally {
      setLoading(false);
    }
  };

  const saveCampaign = async () => {
    if (!recommendations) return;
    setSavingCampaign(true);
    try {
      const priceFrom = parseFloat(parseInputNumber(formData.priceFrom)) || 0;
      const priceTo = parseFloat(parseInputNumber(formData.priceTo)) || priceFrom;
      const avgPrice = priceTo > 0 ? (priceFrom + priceTo) / 2 : priceFrom;
      const budgetValue = parseFloat(parseInputNumber(formData.budget)) || 0;
      const bedroomsFrom = parseInt(formData.bedroomsFrom) || null;
      const bedroomsTo = parseInt(formData.bedroomsTo) || bedroomsFrom;
      
      await axios.post(`${API}/save-campaign`, {
        property: {
          emirate: formData.emirate,
          area: formData.area,
          property_type: formData.propertyType,
          price_aed: avgPrice,
          bedrooms_from: bedroomsFrom,
          bedrooms_to: bedroomsTo
        },
        budget: {
          total_budget_aed: budgetValue,
          campaign_duration_days: formData.duration
        },
        language: language
      });
      toast.success(t.campaignSaved);
    } catch (error) {
      console.error("Error saving campaign:", error);
    } finally {
      setSavingCampaign(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t.copied);
  };

  const exportTargetingOptions = () => {
    if (!recommendations?.targeting_options) return;
    
    const to = recommendations.targeting_options;
    const isAr = language === "ar";
    
    const interests = isAr ? to.interests_ar : to.interests_en;
    const behaviors = isAr ? to.behaviors_ar : to.behaviors_en;
    const placements = isAr ? to.placements_ar : to.placements_en;
    const customAudiences = isAr ? to.custom_audiences_ar : to.custom_audiences_en;
    
    const exportText = isAr ? `
═══════════════════════════════════════════
       خيارات استهداف Meta - AdTarget Pro
═══════════════════════════════════════════

🎯 الاهتمامات (Interests):
${interests?.map(i => `   • ${i}`).join('\n')}

👥 السلوكيات (Behaviors):
${behaviors?.map(b => `   • ${b}`).join('\n')}

📊 البيانات الديموغرافية:
   • الأجهزة: ${to.devices_ar}
   • الفئة العمرية: ${to.age_ar}
   • مستوى الدخل: ${to.income_ar}

📍 المواضع (Placements):
${placements?.map(p => `   • ${p}`).join('\n')}

🎯 الجماهير المخصصة (Advanced):
${customAudiences?.map(c => `   ✓ ${c}`).join('\n')}

═══════════════════════════════════════════
         تم التصدير من AdTarget Pro
═══════════════════════════════════════════
` : `
═══════════════════════════════════════════
       Meta Targeting Options - AdTarget Pro
═══════════════════════════════════════════

🎯 Interests:
${interests?.map(i => `   • ${i}`).join('\n')}

👥 Behaviors:
${behaviors?.map(b => `   • ${b}`).join('\n')}

📊 Demographics:
   • Devices: ${to.devices_en}
   • Age Range: ${to.age_en}
   • Income Level: ${to.income_en}

📍 Placements:
${placements?.map(p => `   • ${p}`).join('\n')}

🎯 Custom Audiences (Advanced):
${customAudiences?.map(c => `   ✓ ${c}`).join('\n')}

═══════════════════════════════════════════
         Exported from AdTarget Pro
═══════════════════════════════════════════
`;
    
    navigator.clipboard.writeText(exportText.trim());
    toast.success(t.exportedSuccess);
  };

  const toggleLanguage = () => {
    const newLanguage = language === "ar" ? "en" : "ar";
    setLanguage(newLanguage);
    
    // If we have recommendations, re-fetch them in the new language
    if (recommendations && formData.emirate && formData.area && formData.propertyType) {
      refetchRecommendations(newLanguage);
    }
  };

  const refetchRecommendations = async (newLanguage) => {
    setLoading(true);
    try {
      const priceFrom = parseFloat(parseInputNumber(formData.priceFrom)) || 0;
      const priceTo = parseFloat(parseInputNumber(formData.priceTo)) || priceFrom;
      const avgPrice = priceTo > 0 ? (priceFrom + priceTo) / 2 : priceFrom;
      const budgetValue = parseFloat(parseInputNumber(formData.budget)) || 0;
      const bedroomsFrom = parseInt(formData.bedroomsFrom) || null;
      const bedroomsTo = parseInt(formData.bedroomsTo) || bedroomsFrom;
      
      const response = await axios.post(`${API}/recommendations`, {
        property: {
          emirate: formData.emirate,
          area: formData.area,
          property_type: formData.propertyType,
          price_aed: avgPrice,
          bedrooms_from: bedroomsFrom,
          bedrooms_to: bedroomsTo
        },
        budget: {
          total_budget_aed: budgetValue,
          campaign_duration_days: formData.duration || null
        },
        campaign_objective: formData.campaignObjective,
        language: newLanguage,
        selected_regions: formData.selectedRegions.length > 0 ? formData.selectedRegions : null,
        num_countries: formData.numCountries
      });
      setRecommendations(response.data);
    } catch (error) {
      console.error("Error refetching:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setRecommendations(null);
    setActiveTab("form");
  };

  const saveForComparison = async () => {
    if (!saveName.trim() || !recommendations) return;
    
    setSavingForComparison(true);
    try {
      const priceFrom = parseFloat(parseInputNumber(formData.priceFrom)) || 0;
      const priceTo = parseFloat(parseInputNumber(formData.priceTo)) || priceFrom;
      const avgPrice = priceTo > 0 ? (priceFrom + priceTo) / 2 : priceFrom;
      const budgetValue = parseFloat(parseInputNumber(formData.budget)) || 0;
      const bedroomsFrom = parseInt(formData.bedroomsFrom) || null;
      const bedroomsTo = parseInt(formData.bedroomsTo) || bedroomsFrom;
      
      await axios.post(`${API}/saved-recommendations`, {
        name: saveName.trim(),
        property: {
          emirate: formData.emirate,
          area: formData.area,
          property_type: formData.propertyType,
          price_aed: avgPrice,
          bedrooms_from: bedroomsFrom,
          bedrooms_to: bedroomsTo
        },
        budget: {
          total_budget_aed: budgetValue,
          campaign_duration_days: formData.duration
        },
        recommendations: recommendations,
        language: language
      });
      toast.success(t.savedForComparison);
      setShowSaveModal(false);
      setSaveName("");
    } catch (error) {
      console.error("Error saving for comparison:", error);
      toast.error(language === "ar" ? "حدث خطأ في الحفظ" : "Error saving recommendation");
    } finally {
      setSavingForComparison(false);
    }
  };

  const toggleRegion = (regionKey) => {
    setFormData(prev => ({
      ...prev,
      selectedRegions: prev.selectedRegions.includes(regionKey)
        ? prev.selectedRegions.filter(r => r !== regionKey)
        : [...prev.selectedRegions, regionKey]
    }));
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat(language === "ar" ? "ar-AE" : "en-AE").format(num);
  };

  // Format number with commas for display in input fields
  const formatInputNumber = (value) => {
    if (!value && value !== 0) return "";
    const num = String(value).replace(/[^\d]/g, "");
    return num ? new Intl.NumberFormat("en-US").format(parseInt(num)) : "";
  };

  // Parse formatted number back to raw number
  const parseInputNumber = (value) => {
    return String(value).replace(/,/g, "");
  };

  // Handle price input change with formatting
  const handlePriceChange = (field, value) => {
    const rawValue = parseInputNumber(value);
    setFormData({...formData, [field]: rawValue});
  };

  // Handle budget input change with formatting
  const handleBudgetChange = (value) => {
    const rawValue = parseInputNumber(value);
    setFormData({...formData, budget: rawValue});
  };

  if (showTracking) {
    return (
      <div className="min-h-screen bg-background">
        <Button variant="ghost" onClick={() => setShowTracking(false)} className="m-4" data-testid="back-to-dashboard">
          <ChevronLeft className="w-4 h-4 mr-2" />
          {language === "ar" ? "العودة للوحة التحكم" : "Back to Dashboard"}
        </Button>
        <CampaignTracking />
      </div>
    );
  }

  if (showComparison) {
    return (
      <CompareRecommendations 
        onBack={() => setShowComparison(false)} 
        language={language} 
      />
    );
  }

  return (
    <div className={`min-h-screen bg-background ${isRTL ? "rtl" : "ltr"}`} dir={isRTL ? "rtl" : "ltr"}>
      {/* Save for Comparison Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Bookmark className="w-5 h-5 text-primary" />
                  {t.saveForComparison}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowSaveModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <CardDescription>{t.enterName}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input 
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder={t.namePlaceholder}
                data-testid="save-name-input"
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowSaveModal(false)}>
                  {t.cancel}
                </Button>
                <Button 
                  onClick={saveForComparison} 
                  disabled={!saveName.trim() || savingForComparison}
                  className="bg-gradient-to-r from-primary to-secondary"
                  data-testid="confirm-save-btn"
                >
                  {savingForComparison ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  {t.saveAndCompare}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3 max-w-full overflow-hidden">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
                <Target className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm sm:text-lg font-bold text-primary truncate">{t.appTitle}</h1>
                <p className="text-[10px] sm:text-xs text-muted-foreground hidden sm:block">{t.appSubtitle}</p>
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-3 flex-wrap justify-end">
              <Button variant="outline" size="sm" onClick={toggleLanguage} className="gap-1 sm:gap-2 px-2 sm:px-3 h-8 sm:h-9 text-xs sm:text-sm" data-testid="language-toggle">
                <Languages className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{language === "ar" ? "EN" : "عربي"}</span>
                <span className="sm:hidden">{language === "ar" ? "EN" : "ع"}</span>
              </Button>

              <Button variant="outline" size="sm" onClick={() => setShowComparison(true)} className="gap-1 sm:gap-2 px-2 sm:px-3 h-8 sm:h-9 text-xs sm:text-sm" data-testid="compare-btn">
                <Scale className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden md:inline">{t.compareRecommendations}</span>
              </Button>

              <Button variant="outline" size="sm" onClick={() => setShowTracking(true)} className="gap-1 sm:gap-2 px-2 sm:px-3 h-8 sm:h-9 text-xs sm:text-sm" data-testid="track-campaigns-btn">
                <Activity className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden md:inline">{t.trackCampaigns}</span>
              </Button>

              <Button variant="outline" size="sm" onClick={() => window.location.href = "/settings"} className="gap-1 sm:gap-2 px-2 sm:px-3 h-8 sm:h-9 text-xs sm:text-sm" data-testid="settings-btn">
                <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
              </Button>

              <Badge 
                variant={metaStatus?.connected ? "default" : "secondary"} 
                className={`gap-1 text-[10px] sm:text-xs px-1.5 sm:px-2 ${metaStatus?.connected ? "bg-green-600" : ""}`} 
                data-testid="meta-status-badge"
              >
                {metaStatus?.connected ? (
                  <><Wifi className="w-2.5 h-2.5 sm:w-3 sm:h-3" /><span className="hidden sm:inline">{t.metaConnected}</span><span className="sm:hidden">Meta</span></>
                ) : (
                  <><WifiOff className="w-2.5 h-2.5 sm:w-3 sm:h-3" /></>
                )}
              </Badge>
            </div>
          </div>

          {metaStatus?.connected && (
            <div className="mt-3 border-t border-border pt-3">
              {/* Date Range Selector */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-primary" />
                  <span className="text-sm text-muted-foreground">{t.dateRange}:</span>
                  <Select value={metaDatePreset} onValueChange={handleDatePresetChange}>
                    <SelectTrigger className="w-40 h-8 text-sm" data-testid="date-preset-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {datePresetOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {language === "ar" ? option.label_ar : option.label_en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Custom Date Range Picker */}
                  {showDatePicker && (
                    <div className="flex items-center gap-2 ml-2">
                      <Input 
                        type="date" 
                        value={metaCustomDates.since}
                        onChange={(e) => setMetaCustomDates(prev => ({...prev, since: e.target.value}))}
                        className="h-8 w-32 text-sm"
                        data-testid="date-from"
                      />
                      <span className="text-muted-foreground text-sm">-</span>
                      <Input 
                        type="date" 
                        value={metaCustomDates.until}
                        onChange={(e) => setMetaCustomDates(prev => ({...prev, until: e.target.value}))}
                        className="h-8 w-32 text-sm"
                        data-testid="date-to"
                      />
                      <Button 
                        size="sm" 
                        onClick={applyCustomDateRange}
                        disabled={!metaCustomDates.since || !metaCustomDates.until}
                        className="h-8"
                        data-testid="apply-date-btn"
                      >
                        {t.apply}
                      </Button>
                    </div>
                  )}
                  
                  {loadingInsights && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
                </div>
                
                <div className="flex items-center gap-1 text-muted-foreground">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span className="text-sm">{metaStatus.account_name}</span>
                </div>
              </div>
              
              {/* Meta Insights Data */}
              {metaInsights && (
                <div className="flex items-center justify-between text-xs sm:text-sm overflow-x-auto">
                  <div className="flex items-center gap-2 sm:gap-4 md:gap-6 flex-wrap">
                    <div className="flex items-center gap-1 sm:gap-2 whitespace-nowrap">
                      <span className="text-muted-foreground">{t.spend}</span>
                      <span className="font-semibold text-secondary">{formatNumber(Math.round(parseFloat(metaInsights.spend || 0)))} {t.aed}</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 whitespace-nowrap">
                      <span className="text-muted-foreground">{t.reach}</span>
                      <span className="font-semibold text-green-500">{formatNumber(parseInt(metaInsights.reach || 0))}</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 whitespace-nowrap">
                      <span className="text-muted-foreground">{t.clicks}</span>
                      <span className="font-semibold text-cyan-500">{formatNumber(parseInt(metaInsights.clicks || 0))}</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 whitespace-nowrap">
                      <span className="text-muted-foreground">CPM</span>
                      <span className="font-semibold text-purple-500">{parseFloat(metaInsights.cpm || 0).toFixed(2)} {t.aed}</span>
                    </div>
                    <div className="hidden sm:flex items-center gap-1 sm:gap-2 whitespace-nowrap">
                      <span className="text-muted-foreground">{language === "ar" ? "مرات الظهور" : "Impressions"}</span>
                      <span className="font-semibold text-orange-500">{formatNumber(parseInt(metaInsights.impressions || 0))}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 max-w-full overflow-hidden">
        <div className="text-center mb-4 sm:mb-8">
          <h2 className="text-xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent mb-2 sm:mb-3 px-2">
            {t.heroTitle}
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground max-w-2xl mx-auto px-2">{t.heroSubtitle}</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="max-w-5xl mx-auto px-1">
          <TabsList className="grid w-full grid-cols-2 mb-4 sm:mb-6 h-auto">
            <TabsTrigger value="form" className="gap-1 sm:gap-2 text-xs sm:text-sm py-2 sm:py-3" data-testid="form-tab">
              <Building2 className="w-3 h-3 sm:w-4 sm:h-4" />{t.formTab}
            </TabsTrigger>
            <TabsTrigger value="results" disabled={!recommendations} className="gap-1 sm:gap-2 text-xs sm:text-sm py-2 sm:py-3" data-testid="results-tab">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />{t.resultsTab}
            </TabsTrigger>
          </TabsList>

          {/* Form Tab */}
          <TabsContent value="form">
            <Card className="border-2 border-primary/20">
              <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  {/* Property Information */}
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center gap-2 text-primary mb-2 sm:mb-4">
                      <Building2 className="w-4 h-4 sm:w-5 sm:h-5" />
                      <h3 className="font-semibold text-sm sm:text-base">{t.propertyInfo}</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                      <div className="space-y-1 sm:space-y-2">
                        <Label className="text-xs sm:text-sm">{t.emirate} *</Label>
                        <Select value={formData.emirate} onValueChange={(v) => setFormData({...formData, emirate: v, area: ""})} data-testid="emirate-select">
                          <SelectTrigger className="h-9 sm:h-10 text-sm"><SelectValue placeholder={t.selectEmirate} /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(emirates).map(([key, data]) => (
                              <SelectItem key={key} value={key}>{data.flag} {language === "ar" ? data.name_ar : data.name_en}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1 sm:space-y-2">
                        <Label className="text-xs sm:text-sm">{t.area} *</Label>
                        <Select value={formData.area} onValueChange={(v) => setFormData({...formData, area: v})} disabled={!formData.emirate} data-testid="area-select">
                          <SelectTrigger className="h-9 sm:h-10 text-sm"><SelectValue placeholder={t.selectArea} /></SelectTrigger>
                          <SelectContent className="max-h-60">
                            {Object.entries(areas).map(([key, data]) => (
                              <SelectItem key={key} value={key}>{language === "ar" ? data.name_ar : data.name_en}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>{t.propertyType} *</Label>
                        <Select value={formData.propertyType} onValueChange={(v) => setFormData({...formData, propertyType: v})} data-testid="property-type-select">
                          <SelectTrigger><SelectValue placeholder={t.selectPropertyType} /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(propertyTypes).map(([key, data]) => (
                              <SelectItem key={key} value={key}>{language === "ar" ? data.name_ar : data.name_en}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>{t.priceRange} *</Label>
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <Input 
                              type="text" 
                              value={formatInputNumber(formData.priceFrom)} 
                              onChange={(e) => handlePriceChange('priceFrom', e.target.value)} 
                              placeholder={t.priceFromPlaceholder}
                              className={`text-sm ${isRTL ? "pl-14" : "pr-14"}`}
                              data-testid="price-from-input" 
                            />
                            <span className={`absolute top-1/2 -translate-y-1/2 text-xs font-semibold text-secondary bg-secondary/10 px-2 py-1 rounded ${isRTL ? "left-1" : "right-1"}`}>
                              AED
                            </span>
                          </div>
                          <span className="text-muted-foreground text-sm font-medium">-</span>
                          <div className="relative flex-1">
                            <Input 
                              type="text" 
                              value={formatInputNumber(formData.priceTo)} 
                              onChange={(e) => handlePriceChange('priceTo', e.target.value)} 
                              placeholder={t.priceToPlaceholder}
                              className={`text-sm ${isRTL ? "pl-14" : "pr-14"}`}
                              data-testid="price-to-input" 
                            />
                            <span className={`absolute top-1/2 -translate-y-1/2 text-xs font-semibold text-secondary bg-secondary/10 px-2 py-1 rounded ${isRTL ? "left-1" : "right-1"}`}>
                              AED
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Bedrooms Range - Only show for property types that need it */}
                      <div className="space-y-2">
                        <Label className={!needsBedrooms ? "text-muted-foreground" : ""}>{t.bedroomsRange}</Label>
                        {needsBedrooms ? (
                          <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                              <Input 
                                type="number" 
                                min="1"
                                max="10"
                                value={formData.bedroomsFrom} 
                                onChange={(e) => setFormData({...formData, bedroomsFrom: e.target.value})} 
                                placeholder={t.bedroomsFromPlaceholder}
                                className="text-sm"
                                data-testid="bedrooms-from-input" 
                              />
                            </div>
                            <span className="text-muted-foreground text-sm font-medium">-</span>
                            <div className="relative flex-1">
                              <Input 
                                type="number" 
                                min="1"
                                max="10"
                                value={formData.bedroomsTo} 
                                onChange={(e) => setFormData({...formData, bedroomsTo: e.target.value})} 
                                placeholder={t.bedroomsToPlaceholder}
                                className="text-sm"
                                data-testid="bedrooms-to-input" 
                              />
                            </div>
                            <BedDouble className="w-5 h-5 text-muted-foreground" />
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 h-10 px-3 bg-muted/50 rounded-md border border-border">
                            <span className="text-muted-foreground text-sm">{t.noBedroomsNeeded}</span>
                            <BedDouble className="w-4 h-4 text-muted-foreground/50" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Budget Information */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-secondary mb-4">
                      <Zap className="w-5 h-5" />
                      <h3 className="font-semibold">{t.budgetSection}</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t.totalBudget} *</Label>
                        <div className="relative">
                          <Input 
                            type="text" 
                            value={formatInputNumber(formData.budget)} 
                            onChange={(e) => handleBudgetChange(e.target.value)} 
                            className={`${isRTL ? "pl-14" : "pr-14"}`}
                            data-testid="budget-input" 
                          />
                          <span className={`absolute top-1/2 -translate-y-1/2 text-xs font-semibold text-secondary bg-secondary/10 px-2 py-1 rounded ${isRTL ? "left-1" : "right-1"}`}>
                            AED
                          </span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{t.campaignDuration}</Label>
                        <Input type="number" value={formData.duration} onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})} placeholder={language === "ar" ? "(تلقائي) 21" : "(Auto) 21"} data-testid="duration-input" />
                      </div>
                    </div>

                    {/* Campaign Objective */}
                    <div className="space-y-2 mt-4">
                      <Label className="text-xs sm:text-sm">{t.campaignObjectiveLabel} *</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                        {campaignObjectives.map((obj) => {
                          const IconComponent = obj.icon;
                          const isSelected = formData.campaignObjective === obj.value;
                          return (
                            <div 
                              key={obj.value}
                              onClick={() => setFormData({...formData, campaignObjective: obj.value})}
                              className={`cursor-pointer p-3 sm:p-4 rounded-xl border-2 transition-all duration-300 transform hover:scale-[1.02] ${
                                isSelected 
                                  ? `border-primary bg-gradient-to-br from-primary/20 to-primary/5 shadow-lg shadow-primary/20` 
                                  : 'border-border hover:border-primary/50 bg-muted/30 hover:bg-muted/50'
                              }`}
                              data-testid={`objective-${obj.value}`}
                            >
                              <div className="flex items-center gap-2 sm:gap-3">
                                <div className={`p-2 sm:p-2.5 rounded-lg ${isSelected ? 'bg-primary/20' : obj.bgColor} transition-colors`}>
                                  <IconComponent className={`w-5 h-5 sm:w-6 sm:h-6 ${isSelected ? 'text-primary' : obj.color} transition-colors`} />
                                </div>
                                <span className={`font-medium text-sm sm:text-base ${isSelected ? 'text-primary' : ''}`}>
                                  {language === "ar" ? obj.label_ar : obj.label_en}
                                </span>
                              </div>
                              {isSelected && (
                                <div className="mt-2 flex items-center gap-1.5">
                                  <CheckCircle2 className="w-3.5 h-3.5 text-primary" />
                                  <span className="text-xs text-primary">{language === "ar" ? "محدد" : "Selected"}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Advanced Targeting Options */}
                  <div className="space-y-3 sm:space-y-4">
                    <Button type="button" variant="ghost" onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-2 text-primary p-0 h-auto text-sm sm:text-base">
                      <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
                      <h3 className="font-semibold">{t.targetingOptions}</h3>
                      {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>

                    {showAdvanced && (
                      <div className="space-y-4 p-3 sm:p-4 bg-muted/30 rounded-lg">
                        {/* Select Countries */}
                        <div className="space-y-2">
                          <Label className="text-sm">{t.selectCountries}</Label>
                          
                          {/* Selected countries display */}
                          {formData.selectedCountries.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2 p-2 bg-muted/50 rounded-lg">
                              {formData.selectedCountries.map(code => {
                                const country = availableCountries.find(c => c.code === code);
                                return (
                                  <Badge key={code} variant="secondary" className="gap-1 text-xs cursor-pointer hover:bg-destructive/20" onClick={() => toggleCountry(code)}>
                                    {country?.flag} {language === "ar" ? country?.name_ar : country?.name_en}
                                    <X className="w-3 h-3" />
                                  </Badge>
                                );
                              })}
                              <Button type="button" variant="ghost" size="sm" className="h-6 text-xs text-destructive" onClick={() => setFormData({...formData, selectedCountries: []})}>
                                {t.clearAll}
                              </Button>
                            </div>
                          )}
                          
                          {/* Countries grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-1">
                            {availableCountries.map((country) => (
                              <div 
                                key={country.code} 
                                onClick={() => toggleCountry(country.code)}
                                className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all text-xs sm:text-sm ${
                                  formData.selectedCountries.includes(country.code) 
                                    ? 'bg-primary/20 border-primary border' 
                                    : 'bg-muted/30 hover:bg-muted/50 border border-transparent'
                                }`}
                              >
                                <span className="text-base">{country.flag}</span>
                                <span className="truncate">{language === "ar" ? country.name_ar : country.name_en}</span>
                              </div>
                            ))}
                          </div>
                          
                          {formData.selectedCountries.length > 0 && (
                            <p className="text-xs text-primary">
                              {formData.selectedCountries.length} {t.countriesSelected}
                            </p>
                          )}
                          {formData.selectedCountries.length === 0 && (
                            <p className="text-xs text-muted-foreground">{t.allRegions}</p>
                          )}
                        </div>

                        <Separator />

                        {/* Select Regions */}
                        <div className="space-y-2">
                          <Label className="text-sm">{t.selectRegions}</Label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {Object.entries(regions).map(([key, data]) => (
                              <div key={key} className="flex items-center gap-2">
                                <Checkbox id={key} checked={formData.selectedRegions.includes(key)} onCheckedChange={() => toggleRegion(key)} className="w-4 h-4" />
                                <label htmlFor={key} className="text-xs sm:text-sm cursor-pointer truncate">
                                  {language === "ar" ? data.name_ar : data.name_en} ({data.countries.length})
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Number of Countries */}
                        <div className="space-y-2">
                          <Label className="text-sm">{t.numCountries}: {formData.numCountries}</Label>
                          <Input type="range" min="3" max="10" value={formData.numCountries} onChange={(e) => setFormData({...formData, numCountries: parseInt(e.target.value)})} className="w-full h-2" />
                        </div>
                      </div>
                    )}
                  </div>

                  <Button type="submit" className="w-full h-11 sm:h-12 text-sm sm:text-lg bg-gradient-to-r from-primary to-secondary hover:opacity-90" disabled={loading} data-testid="submit-btn">
                    {loading ? <><Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin mr-2" />{t.analyzing}</> : <><Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />{t.getRecommendations}</>}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results">
            {recommendations && (
              <div className="space-y-6 relative">
                {/* Loading Overlay when switching language */}
                {loading && (
                  <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-2" />
                      <p className="text-sm text-muted-foreground">{t.analyzing}</p>
                    </div>
                  </div>
                )}
                
                {/* Summary Info */}
                <div className="flex flex-wrap items-center justify-center gap-3 text-muted-foreground">
                  <Badge variant="outline" className="gap-1"><MapPin className="w-3 h-3" />{language === "ar" ? recommendations.emirate_name_ar : recommendations.emirate_name_en}</Badge>
                  <Badge variant="outline" className="gap-1"><Building2 className="w-3 h-3" />{language === "ar" ? recommendations.area_name_ar : recommendations.area_name_en}</Badge>
                  <Badge variant="outline" className="gap-1"><span className="text-xs font-semibold text-secondary">AED</span>{formatNumber(recommendations.total_budget_aed)}</Badge>
                  <Badge variant="outline" className="gap-1 bg-primary/10"><Globe2 className="w-3 h-3" />{recommendations.total_countries_analyzed} {t.countriesAnalyzed}</Badge>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardContent className="pt-4 text-center">
                      <Eye className="w-8 h-8 mx-auto mb-2 text-primary" />
                      <p className="text-2xl font-bold text-primary">{formatNumber(recommendations.total_estimated_reach)}</p>
                      <p className="text-xs text-muted-foreground">{t.expectedReach}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-secondary/10 to-secondary/5 border-secondary/20">
                    <CardContent className="pt-4 text-center">
                      <Users className="w-8 h-8 mx-auto mb-2 text-secondary" />
                      <p className="text-2xl font-bold text-secondary">{formatNumber(recommendations.total_estimated_leads)}</p>
                      <p className="text-xs text-muted-foreground">{t.expectedLeads}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20">
                    <CardContent className="pt-4 text-center">
                      <Calendar className="w-8 h-8 mx-auto mb-2 text-green-500" />
                      <p className="text-2xl font-bold text-green-500">{recommendations.recommended_duration_days}</p>
                      <p className="text-xs text-muted-foreground">{t.days}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 border-cyan-500/20">
                    <CardContent className="pt-4 text-center">
                      <TrendingUp className="w-8 h-8 mx-auto mb-2 text-cyan-500" />
                      <p className="text-2xl font-bold text-cyan-500">{recommendations.campaign_objective.expected_ctr}%</p>
                      <p className="text-xs text-muted-foreground">{t.expectedCTR}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Target Countries */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Globe2 className="w-5 h-5 text-primary" />{t.targetCountries}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        {recommendations.countries.map((country, idx) => (
                          <div key={country.country_code} className="p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors" data-testid={`country-${country.country_code}`}>
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{country.flag}</span>
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium">{language === "ar" ? country.country_name_ar : country.country_name_en}</span>
                                  <Badge style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}>{country.budget_percentage}%</Badge>
                                </div>
                                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-1">
                                  <span>{formatNumber(country.budget_allocation_aed)} {t.aed}</span>
                                  <span>{formatNumber(country.estimated_reach)} {t.reach}</span>
                                  <span>{country.estimated_leads} {t.leads}</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{language === "ar" ? country.reasoning_ar : country.reasoning_en}</p>
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {country.recommended_ad_types.map(adType => (
                                    <Badge key={adType} variant="outline" className="text-xs">{adType}</Badge>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <RePieChart>
                            <Pie data={recommendations.countries.map((c, i) => ({name: language === "ar" ? c.country_name_ar : c.country_name_en, value: c.budget_percentage, fill: CHART_COLORS[i % CHART_COLORS.length]}))} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                              {recommendations.countries.map((_, i) => (<Cell key={`cell-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />))}
                            </Pie>
                            <Tooltip />
                          </RePieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* AI Insights */}
                {recommendations.ai_insights && (
                  <Card className="border-primary/30 bg-gradient-to-r from-primary/5 to-secondary/5">
                    <CardHeader><CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />{t.aiAnalysis}</CardTitle></CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap" data-testid="ai-insights">{recommendations.ai_insights}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Meta Targeting Options */}
                {recommendations.targeting_options && (
                  <Card className="border-accent/30" data-testid="targeting-options-card">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                            <Target className="w-4 h-4 sm:w-5 sm:h-5 text-accent flex-shrink-0" />
                            <span className="truncate">{t.metaTargetingOptions}</span>
                          </CardTitle>
                          <CardDescription className="text-xs sm:text-sm mt-1">
                            {language === "ar" ? "خيارات الاستهداف المقترحة لحملتك على Meta" : "Suggested targeting options for your Meta campaign"}
                          </CardDescription>
                        </div>
                        <Button 
                          onClick={exportTargetingOptions}
                          className="bg-accent hover:bg-accent/80 text-accent-foreground text-xs sm:text-sm px-2 sm:px-4 h-8 sm:h-10 flex-shrink-0"
                          data-testid="export-targeting-btn"
                        >
                          <Copy className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2" />
                          <span className="hidden sm:inline">{t.exportTargeting}</span>
                          <span className="sm:hidden">{t.copySection}</span>
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="px-3 sm:px-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {/* Interests */}
                        <div className="p-3 sm:p-4 rounded-lg bg-muted/30 border border-border">
                          <div className="flex items-center gap-2 mb-2 sm:mb-3">
                            <span className="text-lg sm:text-xl">🎯</span>
                            <h4 className="font-semibold text-sm sm:text-base">{t.interests}</h4>
                          </div>
                          <div className="flex flex-wrap gap-1.5 sm:gap-2">
                            {(language === "ar" ? recommendations.targeting_options.interests_ar : recommendations.targeting_options.interests_en)?.map((interest, i) => (
                              <Badge key={i} variant="outline" className="text-[10px] sm:text-xs">{interest}</Badge>
                            ))}
                          </div>
                        </div>

                        {/* Behaviors */}
                        <div className="p-4 rounded-lg bg-muted/30 border border-border">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-lg sm:text-xl">👥</span>
                            <h4 className="font-semibold text-sm sm:text-base">{t.behaviors}</h4>
                          </div>
                          <div className="flex flex-wrap gap-1.5 sm:gap-2">
                            {(language === "ar" ? recommendations.targeting_options.behaviors_ar : recommendations.targeting_options.behaviors_en)?.map((behavior, i) => (
                              <Badge key={i} variant="outline" className="text-[10px] sm:text-xs">{behavior}</Badge>
                            ))}
                          </div>
                        </div>

                        {/* Demographics */}
                        <div className="p-3 sm:p-4 rounded-lg bg-muted/30 border border-border">
                          <div className="flex items-center gap-2 mb-2 sm:mb-3">
                            <span className="text-lg sm:text-xl">📊</span>
                            <h4 className="font-semibold text-sm sm:text-base">{language === "ar" ? "البيانات الديموغرافية" : "Demographics"}</h4>
                          </div>
                          <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5">
                              <span className="text-muted-foreground">{t.devices}:</span>
                              <span className="font-medium text-accent text-[11px] sm:text-sm">{language === "ar" ? recommendations.targeting_options.devices_ar : recommendations.targeting_options.devices_en}</span>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5">
                              <span className="text-muted-foreground">{t.ageRange}:</span>
                              <span className="font-medium text-[11px] sm:text-sm">{language === "ar" ? recommendations.targeting_options.age_ar : recommendations.targeting_options.age_en}</span>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5">
                              <span className="text-muted-foreground">{t.incomeLevel}:</span>
                              <span className="font-medium text-primary text-[11px] sm:text-sm">{language === "ar" ? recommendations.targeting_options.income_ar : recommendations.targeting_options.income_en}</span>
                            </div>
                          </div>
                        </div>

                        {/* Placements */}
                        <div className="p-3 sm:p-4 rounded-lg bg-muted/30 border border-border">
                          <div className="flex items-center gap-2 mb-2 sm:mb-3">
                            <span className="text-lg sm:text-xl">📍</span>
                            <h4 className="font-semibold text-sm sm:text-base">{t.placements}</h4>
                          </div>
                          <div className="flex flex-wrap gap-1.5 sm:gap-2">
                            {(language === "ar" ? recommendations.targeting_options.placements_ar : recommendations.targeting_options.placements_en)?.map((placement, i) => (
                              <Badge key={i} className="bg-secondary/20 text-secondary-foreground text-[10px] sm:text-xs">{placement}</Badge>
                            ))}
                          </div>
                        </div>

                        {/* Custom Audiences */}
                        <div className="p-3 sm:p-4 rounded-lg bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 sm:col-span-2">
                          <div className="flex items-center gap-2 mb-2 sm:mb-3 flex-wrap">
                            <span className="text-lg sm:text-xl">🎯</span>
                            <h4 className="font-semibold text-sm sm:text-base">{t.customAudiences}</h4>
                            <Badge className="bg-primary text-[10px] sm:text-xs">{language === "ar" ? "متقدم" : "Advanced"}</Badge>
                          </div>
                          <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                            {(language === "ar" ? recommendations.targeting_options.custom_audiences_ar : recommendations.targeting_options.custom_audiences_en)?.map((audience, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                <span>{audience}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Ad Types Section */}
                <Card>
                  <CardHeader className="px-3 sm:px-6">
                    <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                      <Video className="w-4 h-4 sm:w-5 sm:h-5 text-secondary" />{t.allAdTypes}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm">{language === "ar" ? "جميع أنواع الإعلانات المتاحة مع التفاصيل" : "All available ad types with details"}</CardDescription>
                  </CardHeader>
                  <CardContent className="px-3 sm:px-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                      {recommendations.all_ad_types.map((adType) => (
                        <div key={adType.type} className={`p-4 rounded-lg border-2 transition-all ${adType.is_recommended ? 'border-primary bg-primary/5' : 'border-border bg-muted/30'}`}>
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-2xl">{adType.icon}</span>
                            {adType.is_recommended && <Badge className="bg-primary">{t.recommended}</Badge>}
                          </div>
                          <h4 className="font-semibold mb-1">{language === "ar" ? adType.name_ar : adType.name_en}</h4>
                          <p className="text-sm text-muted-foreground mb-3">{language === "ar" ? adType.description_ar : adType.description_en}</p>
                          
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">CTR:</span>
                              <span className="font-medium text-green-500">{adType.avg_ctr}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">{t.minBudget}:</span>
                              <span className="font-medium">{formatNumber(adType.recommended_budget_min_aed)} {t.aed}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">{t.bestPlatforms}:</span>
                              <div className="flex gap-1">
                                {adType.best_platforms.slice(0, 3).map(p => (
                                  <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                                ))}
                              </div>
                            </div>
                          </div>

                          <Button variant="ghost" size="sm" className="w-full mt-3" onClick={() => setExpandedAdType(expandedAdType === adType.type ? null : adType.type)}>
                            {expandedAdType === adType.type ? t.hideDetails : t.showDetails}
                            {expandedAdType === adType.type ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                          </Button>

                          {expandedAdType === adType.type && (
                            <div className="mt-3 pt-3 border-t border-border space-y-2">
                              <p className="text-sm font-medium">{t.creativeTips}:</p>
                              <ul className="text-xs text-muted-foreground space-y-1">
                                {(language === "ar" ? adType.creative_tips_ar : adType.creative_tips_en).map((tip, i) => (
                                  <li key={i}>• {tip}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Ad Copy Suggestions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-cyan-500" />{t.adCopySuggestions}</CardTitle>
                    <CardDescription>{t.clickToCopy}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {recommendations.ad_copy_suggestions.map((copy, idx) => (
                        <div key={idx} onClick={() => copyToClipboard(copy)} className="p-3 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer transition-colors border border-transparent hover:border-primary/30" data-testid={`ad-copy-${idx}`}>
                          <p className="text-sm">{copy}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="flex gap-4 justify-center flex-wrap">
                  <Button variant="outline" onClick={() => setActiveTab("form")} className="gap-2" data-testid="edit-data-btn">
                    <ChevronLeft className="w-4 h-4" />{t.editData}
                  </Button>
                  <Button variant="secondary" onClick={handleReset} className="gap-2" data-testid="new-analysis-btn">
                    <Sparkles className="w-4 h-4" />{t.newAnalysis}
                  </Button>
                  <Button variant="outline" onClick={() => setShowSaveModal(true)} className="gap-2 border-cyan-500 text-cyan-500 hover:bg-cyan-500/10" data-testid="save-for-comparison-btn">
                    <Bookmark className="w-4 h-4" />{t.saveForComparison}
                  </Button>
                  <Button onClick={saveCampaign} disabled={savingCampaign} className="gap-2 bg-gradient-to-r from-primary to-secondary" data-testid="save-campaign-btn">
                    {savingCampaign ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{t.saveForTracking}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <footer className="bg-card border-t border-border py-4 mt-8">
        <p className="text-center text-sm text-muted-foreground">{t.footerText}</p>
      </footer>
    </div>
  );
};

export default Dashboard;
